import { Alert, Snackbar } from '@mui/material';
import React from 'react';

interface BkSnackBarProps {
  handleCloseSnackbar: () => void;
  open: boolean;
  snackMessage: string;
  severity?: 'error' | 'success' | 'info' | 'warning';
}

/**
 * Splits a message string into text and URL segments and renders URLs as
 * clickable anchor tags that open in a new tab.
 *
 * Example: "Something went wrong. Visit https://example.com to fix it."
 * becomes: "Something went wrong. Visit " + <a href="https://example.com">https://example.com</a> + " to fix it."
 */
function linkifyMessage(message: string): React.ReactNode {
  if (!message) return message;
  const URL_PATTERN = /(https?:\/\/[^\s]+)/g;
  const parts = message.split(URL_PATTERN);

  if (parts.length === 1) return message;

  return (
    <>
      {parts.map((part, i) =>
        URL_PATTERN.test(part) ? (
          <a
            key={i}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'inherit', fontWeight: 'bold', wordBreak: 'break-all' }}
          >
            {part}
          </a>
        ) : (
          part
        ),
      )}
    </>
  );
}

const BkSnackBar: React.FC<BkSnackBarProps> = ({ open, snackMessage, handleCloseSnackbar, severity = 'error' }) => {
  // Give users more time to read messages that contain a clickable link.
  const hasLink = /(https?:\/\/[^\s]+)/.test(snackMessage);
  const autoHideDuration = hasLink ? 20000 : 6000;

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      autoHideDuration={autoHideDuration}
      onClose={handleCloseSnackbar}
    >
      <Alert
        onClose={handleCloseSnackbar}
        variant="filled"
        severity={severity}
        sx={{ width: '100%', textAlign: 'center' }}
      >
        {linkifyMessage(snackMessage)}
      </Alert>
    </Snackbar>
  );
};

export default BkSnackBar;
